/**
 * 外車專用 API client（openspec add-external-driver-grab）
 *
 * 與 services/apiClient.ts 完全分開：自己的 token 儲存鍵、自己的 401/403 處理。
 * 會員司機的 apiClient 在 403 時會導去 /binding（司機編號＋驗證碼），外車不能被那條路抓走。
 *
 * 登入：以 LiffContext 存在 localStorage 的 LINE ID Token 打 /line/ext/auth/login；
 *   bound=true  → 存 EXTERNAL_DRIVER token
 *   bound=false → 存 LINE_USER token（只夠打綁定端點），丟 ExtNotBoundError 讓頁面導去 /ext/bind
 * 不發 refresh token：EXTERNAL_DRIVER token 過期（7 天）就重打一次 login。
 */
import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import type { ApiResponse } from '@/types'
import type { ExtDriver, ExtLoginResponse } from './types'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api'

export const EXT_KEYS = {
  accessToken: 'towconnect-ext-access-token',
  accessTokenExpires: 'towconnect-ext-access-token-expires',
  lineUserToken: 'towconnect-ext-line-user-token',
  lineIdToken: 'towconnect-liff-id-token', // 由 LiffContext 寫入，這裡只讀
} as const

export class ExtApiError extends Error {
  code: string
  constructor(code: string, message: string) {
    super(message)
    this.name = 'ExtApiError'
    this.code = code
  }
}

/** LINE 已登入但尚未綁定外車資料 */
export class ExtNotBoundError extends Error {
  /** 該 LINE id 曾綁過（停用中）的既有資料，綁定表單用來預填 */
  existing: ExtDriver | null
  constructor(existing: ExtDriver | null = null) {
    super('尚未綁定')
    this.name = 'ExtNotBoundError'
    this.existing = existing
  }
}

/** 取不到 LINE ID Token（LIFF 未登入或已過期） */
export class ExtNoIdTokenError extends Error {
  constructor() {
    super('缺少 LINE ID Token')
    this.name = 'ExtNoIdTokenError'
  }
}

export const ERR_PLATFORM_DRIVER = 'LNE-0405'
export const ERR_LINK_INVALID = 'LNE-0401'
export const ERR_LINE_AUTH_FAILED = 'LNE-0105'

const raw = axios.create({ baseURL: API_BASE_URL, timeout: 30000, headers: { 'Content-Type': 'application/json' } })

function getValidExtToken(): string | null {
  const token = localStorage.getItem(EXT_KEYS.accessToken)
  const expires = localStorage.getItem(EXT_KEYS.accessTokenExpires)
  if (token && expires && Date.now() < parseInt(expires, 10) - 30000) return token
  return null
}

export function saveExtToken(token: string, expiresInSeconds: number): void {
  localStorage.setItem(EXT_KEYS.accessToken, token)
  localStorage.setItem(EXT_KEYS.accessTokenExpires, String(Date.now() + expiresInSeconds * 1000))
}

export function clearExtToken(): void {
  localStorage.removeItem(EXT_KEYS.accessToken)
  localStorage.removeItem(EXT_KEYS.accessTokenExpires)
}

export function getLineUserToken(): string | null {
  return localStorage.getItem(EXT_KEYS.lineUserToken)
}

/**
 * 以 LINE ID Token 登入外車端。成功綁定 → 存 token 並回傳；未綁定 → 丟 ExtNotBoundError。
 * 平台司機 / ID Token 失效 → 丟 ExtApiError（code 由頁面判斷）。
 */
export async function extLogin(): Promise<ExtLoginResponse> {
  const idToken = localStorage.getItem(EXT_KEYS.lineIdToken)
  if (!idToken) throw new ExtNoIdTokenError()
  const res = await raw.post<ApiResponse<ExtLoginResponse>>('/line/ext/auth/login', {}, {
    headers: { Authorization: `Bearer ${idToken}` },
  })
  const body = res.data
  if (body.statusCode !== '200' || !body.data) {
    throw new ExtApiError(body.statusCode, body.statusDesc || '登入失敗')
  }
  const data = body.data
  if (data.bound) {
    saveExtToken(data.accessToken, data.accessTokenExpiresIn)
    localStorage.removeItem(EXT_KEYS.lineUserToken)
    return data
  }
  localStorage.setItem(EXT_KEYS.lineUserToken, data.accessToken)
  clearExtToken()
  throw new ExtNotBoundError(data.driver ?? null)
}

/** 確保手上有可用的 EXTERNAL_DRIVER token；沒有就重登 */
export async function ensureExtSession(): Promise<string> {
  const existing = getValidExtToken()
  if (existing) return existing
  clearExtToken()
  await extLogin()
  const token = getValidExtToken()
  if (!token) throw new ExtNotBoundError()
  return token
}

// ========== 帶 token 的 client ==========

const client: AxiosInstance = axios.create({ baseURL: API_BASE_URL, timeout: 30000, headers: { 'Content-Type': 'application/json' } })

client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const token = await ensureExtSession()
  config.headers['Authorization'] = `Bearer ${token}`
  return config
})

client.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined
    const status = error.response?.status
    // token 被後端拒絕（過期／簽章對不上）：清掉、重登一次、重送一次。仍失敗就交給呼叫端。
    if ((status === 401 || status === 403) && original && !original._retried) {
      original._retried = true
      clearExtToken()
      const token = await ensureExtSession() // 未綁定會丟 ExtNotBoundError，頁面接手
      original.headers['Authorization'] = `Bearer ${token}`
      return client.request(original)
    }
    return Promise.reject(error)
  }
)

export const ERR_NOT_BOUND = 'LNE-0406'

function unwrap<T>(body: ApiResponse<T>): T {
  if (body.statusCode !== '200') {
    // 後端說「尚未綁定」（被後台停用、或車號被別人接手）：手上的 token 已無意義，
    // 清掉並當成未綁定，讓頁面導去 /ext/bind 重新填車號姓名（重綁會重新啟用）
    if (body.statusCode === ERR_NOT_BOUND) {
      clearExtToken()
      throw new ExtNotBoundError()
    }
    throw new ExtApiError(body.statusCode, body.statusDesc || '操作失敗')
  }
  return body.data as T
}

export async function extGet<T>(url: string): Promise<T> {
  const res = await client.get<ApiResponse<T>>(url)
  return unwrap(res.data)
}

export async function extPost<T>(url: string, body?: unknown): Promise<T> {
  const res = await client.post<ApiResponse<T>>(url, body ?? {})
  return unwrap(res.data)
}

export async function extPut<T>(url: string, body?: unknown): Promise<T> {
  const res = await client.put<ApiResponse<T>>(url, body ?? {})
  return unwrap(res.data)
}

/** 綁定端點用 LINE_USER token（未綁定）或 EXTERNAL_DRIVER token（改資料時走 PUT /drivers/me，不經此） */
export async function extBindPost<T>(url: string, body: unknown): Promise<T> {
  const token = getLineUserToken() ?? getValidExtToken()
  if (!token) throw new ExtNoIdTokenError()
  const res = await raw.post<ApiResponse<T>>(url, body, { headers: { Authorization: `Bearer ${token}` } })
  return unwrap(res.data)
}

/** 把 axios / 業務錯誤統一成給使用者看的文字 */
export function extErrorMessage(err: unknown, fallback = '系統發生錯誤，請稍後再試'): string {
  if (err instanceof ExtApiError) return err.message
  if (err instanceof ExtNotBoundError) return '尚未綁定，請先填寫車號與姓名'
  if (err instanceof ExtNoIdTokenError) return '請重新開啟連結完成 LINE 登入'
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as Partial<ApiResponse<unknown>> | undefined
    if (body?.statusDesc) return body.statusDesc
    if (!err.response) return '網路連線失敗，請檢查網路後再試'
  }
  return fallback
}
